import axios from 'axios';
import AppContext from '../../../../context/AppContext';
import React, { useContext, useEffect, useState } from 'react';
import { Button, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import {
    RiAddLine,
    RiArrowLeftLine,
    RiCalendarLine,
    RiCheckboxLine,
    RiCloseLine,
    RiDeleteBinLine,
    RiEditLine,
    RiEyeLine,
    RiPauseLine,
    RiPlayLine,
    RiTimeLine,
    RiUpload2Line,
} from 'react-icons/ri';
import '../../../../assests/css/admin/SaleManagement2/SaleManagement2.css';

/* ─────────────────────────────────────────────
   STATUS BADGE
───────────────────────────────────────────── */
const StatusBadge = ({ status }) => {
    const cls =
        status === 'active'
            ? 'Sale-management-salae-status-badge--active'
            : status === 'scheduled'
                ? 'Sale-management-salae-status-badge--scheduled'
                : 'Sale-management-salae-status-badge--paused';

    return (
        <span className={`Sale-management-salae-status-badge ${cls}`}>
            {status}
        </span>
    );
};

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */
const SaleManagement2 = () => {
    const { baseUrl, baseUrlForFiles } = useContext(AppContext);

    /* ── View State ── */
    const [currentView, setCurrentView] = useState('list');

    /* ── Form States ── */
    const [categories, setCategories] = useState([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState('');
    const [discountPercentage, setDiscountPercentage] = useState('');
    const [btnLabel, setBtnLabel] = useState('Publish Banner');
    const [idToUpdateDelete, setIdToUpdateDelete] = useState('');
    const [bannerTitle, setBannerTitle] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [desktopFile, setDesktopFile] = useState(null);
    const [addBannerLoading, setAddBannerLoading] = useState(false);

    /* ── Image Preview URLs ── */
    const [desktopPreviewUrl, setDesktopPreviewUrl] = useState('');

    /* ── List States ── */
    const [bannerRecords, setBannerRecords] = useState([]);

    /* ══════════════════════════════════════════
       HELPERS
    ══════════════════════════════════════════ */

    const resetForm = () => {
        setIdToUpdateDelete('');
        setBannerTitle('');
        setStartDate('');
        setEndDate('');
        setDesktopFile(null);
        setDesktopPreviewUrl('');
        setSelectedCategoryId('');
        setDiscountPercentage('');
    };

    const removeDesktopFile = () => {
        setDesktopFile(null);
        setDesktopPreviewUrl('');
    };

    /* ══════════════════════════════════════════
       API FUNCTIONS
    ══════════════════════════════════════════ */

    /* VIEW ALL */
    const viewAllBanners = () => {
        axios.get(`${baseUrl}/SaleManagement/Banner/viewAllBanners`)
            .then((response) => {
                setBannerRecords(response.data.bannerRecords || []);
            })
            .catch(() => {});
    };
    const viewAllCategories = () => {
        axios.get(`${baseUrl}/admin/category/getAllCategories`)
            .then((response) => setCategories(response.data.data || []))
            .catch(() => {});
    };

    useEffect(() => {
        viewAllBanners();
        viewAllCategories();
    }, []);

    /* ADD */
    const addBanner = () => {
        const formData = new FormData();
        formData.append('bannerTitle', bannerTitle);
        formData.append('startDate', startDate);
        formData.append('endDate', endDate);
        formData.append('mlcategory_id', selectedCategoryId);
        formData.append('discount_percentage', discountPercentage);
        if (desktopFile) formData.append('desktopFile', desktopFile);

        axios.post(`${baseUrl}/SaleManagement/Banner/addBanner`, formData)
            .then(() => {
                setAddBannerLoading(false);
                viewAllBanners();
                setCurrentView('list');
            })
            .catch(() => setAddBannerLoading(false));
    };

    /* UPDATE */
    const updateBanner = () => {
        const formData = new FormData();
        formData.append('id', idToUpdateDelete);
        formData.append('bannerTitle', bannerTitle);
        formData.append('startDate', startDate);
        formData.append('endDate', endDate);
        formData.append('mlcategory_id', selectedCategoryId);
        formData.append('discount_percentage', discountPercentage);
        if (desktopFile) formData.append('desktopFile', desktopFile);

        axios.post(`${baseUrl}/SaleManagement/Banner/updateBanner`, formData)
            .then((response) => {
                console.log(response.data);
                setAddBannerLoading(false);
                viewAllBanners();
                setCurrentView('list');
            })
            .catch((error) => {
                console.log(error);
                setAddBannerLoading(false);
            });
    };

    /* DELETE */
    const deleteBanner = (idToDelete) => {
        const formData = new FormData();
        formData.append('id', idToDelete);

        axios.post(`${baseUrl}/SaleManagement/Banner/deleteBanner`, formData)
            .then((response) => {
                console.log(response.data);
                viewAllBanners();
            })
            .catch((error) => {
                console.log(error);
            });
    };

    /* PAUSE / RESUME */
    const updateBannerStatus = (idToUpdate, currentStatus) => {
        const newStatus = currentStatus === 'active' ? 'paused' : 'active';
        const formData = new FormData();
        formData.append('id', idToUpdate);
        formData.append('status', newStatus);

        axios.post(`${baseUrl}/SaleManagement/Banner/updateBannerStatus`, formData)
            .then(() => viewAllBanners())
            .catch(() => {});
    };

    /* ══════════════════════════════════════════
       COMPUTED
    ══════════════════════════════════════════ */

    const activeBannersCount = bannerRecords.filter((b) => b.status === 'active').length;
    const scheduledBannersCount = bannerRecords.filter((b) => b.status === 'scheduled').length;

    /* ══════════════════════════════════════════
       RENDER — LIST VIEW
    ══════════════════════════════════════════ */
    if (currentView === 'list') {
        return (
            <div className="Sale-management-salae-wrapper Sale-management-salae-list-view">
                <Container fluid>

                    {/* Header */}
                    <div className="Sale-management-salae-header">
                        <div>
                            <h1 className="Sale-management-salae-title">Sales Banner Management</h1>
                            <p className="Sale-management-salae-subtitle">
                                Curate and schedule high-impact visuals for your storefront hero section.
                            </p>
                        </div>
                        <Button
                            className="Sale-management-salae-add-btn"
                            onClick={() => {
                                resetForm();
                                setBtnLabel('Publish Banner');
                                setCurrentView('add');
                            }}
                        >
                            <RiAddLine /> Add New Banner
                        </Button>
                    </div>

                    {/* Stat Cards */}
                    <Row className="Sale-management-salae-stats-row g-3">
                        <Col md={4}>
                            <div className="Sale-management-salae-stat-card">
                                <div className="Sale-management-salae-stat-icon Sale-management-salae-stat-icon--teal">
                                    <RiEyeLine />
                                </div>
                                <div>
                                    <p className="Sale-management-salae-stat-label">Active Banners</p>
                                    <p className="Sale-management-salae-stat-value">{activeBannersCount}</p>
                                    <p className="Sale-management-salae-stat-sub Sale-management-salae-stat-sub--green">+12.5%</p>
                                </div>
                            </div>
                        </Col>
                        <Col md={4}>
                            <div className="Sale-management-salae-stat-card">
                                <div className="Sale-management-salae-stat-icon Sale-management-salae-stat-icon--orange">
                                    <RiTimeLine />
                                </div>
                                <div>
                                    <p className="Sale-management-salae-stat-label">Upcoming Sales</p>
                                    <p className="Sale-management-salae-stat-value">{scheduledBannersCount}</p>
                                    <p className="Sale-management-salae-stat-sub Sale-management-salae-stat-sub--neutral">Stable</p>
                                </div>
                            </div>
                        </Col>
                        <Col md={4}>
                            <div className="Sale-management-salae-stat-card">
                                <div className="Sale-management-salae-stat-icon Sale-management-salae-stat-icon--purple">
                                    <RiCheckboxLine />
                                </div>
                                <div>
                                    <p className="Sale-management-salae-stat-label">Avg. Click-Through Rate</p>
                                    <p className="Sale-management-salae-stat-value">3.24%</p>
                                    <p className="Sale-management-salae-stat-sub Sale-management-salae-stat-sub--green">+0.4%</p>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* Section Header */}
                    <div className="Sale-management-salae-section-header">
                        <h5 className="Sale-management-salae-section-title">Currently Active &amp; Scheduled</h5>
                    </div>

                    {/* Banner Cards */}
                    {bannerRecords.length === 0 ? (
                        <div className="Sale-management-salae-empty">
                            <p>No banners found. Click "Add New Banner" to get started.</p>
                        </div>
                    ) : (
                        <Row className="g-3">
                            {bannerRecords.map((item) => (
                                <Col key={item.id} md={4}>
                                    <div className="Sale-management-salae-card">

                                        {/* Image */}
                                        <div className="Sale-management-salae-card-img-wrap">
                                            <img
                                                src={`${baseUrlForFiles}/${item.desktopFile}`}
                                                alt={item.bannerTitle}
                                            />
                                            <StatusBadge status={item.status} />
                                        </div>

                                        {/* Body */}
                                        <div className="Sale-management-salae-card-body">
                                            <h6 className="Sale-management-salae-card-title">{item.bannerTitle}</h6>
                                            <p className="Sale-management-salae-card-id">ID: #{item.id}</p>
                                            <p className="Sale-management-salae-card-date">
                                                <RiCalendarLine />
                                                {item.startDate} – {item.endDate}
                                            </p>
                                            <p className="Sale-management-salae-clicks-label">Total Clicks</p>
                                            <p className="Sale-management-salae-clicks-val">
                                                {item.totalClicks != null ? Number(item.totalClicks).toLocaleString() : '—'}
                                            </p>

                                            <hr className="Sale-management-salae-divider" />

                                            {/* Action Buttons */}
                                            <div className="Sale-management-salae-actions">

                                                {/* EDIT */}
                                                {/* EDIT */}
                                                <button
                                                    className="Sale-management-salae-btn Sale-management-salae-btn--edit btn"
                                                    onClick={() => {
                                                        setIdToUpdateDelete(item.id);
                                                        setBannerTitle(item.bannerTitle);
                                                        setStartDate(item.startDate || '');
                                                        setEndDate(item.endDate || '');
                                                        setDesktopFile(null);
                                                        setDesktopPreviewUrl(item.desktopFile ? `${baseUrlForFiles}/${item.desktopFile}` : '');
                                                        setSelectedCategoryId(item.mlcategory_id || '');
                                                        setDiscountPercentage(item.discount_percentage || '');
                                                        setBtnLabel('Update Banner');
                                                        setCurrentView('edit');
                                                    }}
                                                >
                                                    <RiEditLine /> Edit
                                                </button>

                                                {/* PAUSE / RESUME */}
                                                {item.status === 'active' ? (
                                                    <button
                                                        className="Sale-management-salae-btn Sale-management-salae-btn--pause btn"
                                                        onClick={() => updateBannerStatus(item.id, item.status)}
                                                    >
                                                        <RiPauseLine /> Pause
                                                    </button>
                                                ) : (
                                                    <button
                                                        className="Sale-management-salae-btn Sale-management-salae-btn--resume btn"
                                                        onClick={() => updateBannerStatus(item.id, item.status)}
                                                    >
                                                        <RiPlayLine /> Resume
                                                    </button>
                                                )}

                                                {/* DELETE */}
                                                <button
                                                    className="Sale-management-salae-btn Sale-management-salae-btn--delete btn"
                                                    onClick={() => deleteBanner(item.id)}
                                                >
                                                    <RiDeleteBinLine /> Delete
                                                </button>

                                            </div>
                                        </div>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    )}

                </Container>
            </div>
        );
    }

    /* ══════════════════════════════════════════
       RENDER — ADD / EDIT FULL PAGE
    ══════════════════════════════════════════ */
    return (
        <div className="Sale-management-salae-form-page Sale-management-salae-form-view">
            <Container fluid style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

                {/* Back */}
                <Button
                    className="Sale-management-salae-back-btn"
                    onClick={() => setCurrentView('list')}
                >
                    <RiArrowLeftLine /> Back to Banners
                </Button>

                {/* Title */}
                <h1 className="Sale-management-salae-form-page-title">
                    {btnLabel === 'Update Banner' ? 'Edit Banner' : 'Add New Banner'}
                </h1>
                <p className="Sale-management-salae-form-page-subtitle">
                    {btnLabel === 'Update Banner'
                        ? 'Update your banner details and visual asset below.'
                        : 'Fill in the details and upload your visual to publish a new banner.'}
                </p>

                {/* Centered single-column layout */}
                <div className="Sale-management-salae-form-layout Sale-management-salae-form-layout--single">

                    {/* ════ Form Card ════ */}
                    <div className="Sale-management-salae-form-card Sale-management-salae-form-card--full">
                        <div className="Sale-management-salae-form-card-body">

                            {/* Step 1 */}
                            <div className="Sale-management-salae-step-label">
                                <span className="Sale-management-salae-step-num">1</span>
                                General Details
                            </div>

                            {/* Banner Title */}
                            {/* Banner Title */}
                            <Form.Group className="mb-3">
                                <label className="Sale-management-salae-field-label">Banner Title</label>
                                <Form.Control
                                    className="Sale-management-salae-input"
                                    placeholder="e.g. Summer Season Launch"
                                    value={bannerTitle}
                                    onChange={(e) => setBannerTitle(e.target.value)}
                                />
                            </Form.Group>

                            {/* new  category or discount ko add kia */}
                            <Row className="mb-3">
                                <Col>
                                    <label className="Sale-management-salae-field-label">Category</label>
                                    <Form.Select
                                        className="Sale-management-salae-input"
                                        value={selectedCategoryId}
                                        onChange={(e) => setSelectedCategoryId(e.target.value)}
                                    >
                                        <option value="">Select Category</option>
                                        {categories.map((cat) => (
                                            <option key={cat.id} value={cat.id}>{cat.category_name}</option>
                                        ))}
                                    </Form.Select>
                                </Col>
                                <Col>
                                    <label className="Sale-management-salae-field-label">Discount Percentage</label>
                                    <Form.Control
                                        type="number"
                                        className="Sale-management-salae-input"
                                        placeholder="e.g. 20"
                                        value={discountPercentage}
                                        onChange={(e) => setDiscountPercentage(e.target.value)}
                                        min="0" max="100"
                                    />
                                </Col>
                            </Row>

                            {/* Start & End Date */}
                            <Row className="mb-3">
                                <Col>
                                    <label className="Sale-management-salae-field-label">Start Date</label>
                                    <Form.Control
                                        type="date"
                                        className="Sale-management-salae-input"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                    />
                                </Col>
                                <Col>
                                    <label className="Sale-management-salae-field-label">End Date</label>
                                    <Form.Control
                                        type="date"
                                        className="Sale-management-salae-input"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                    />
                                </Col>
                            </Row>

                            <hr className="Sale-management-salae-section-divider" />

                            {/* Step 2 */}
                            <div className="Sale-management-salae-step-label">
                                <span className="Sale-management-salae-step-num">2</span>
                                Visual Asset
                            </div>

                            {/* Desktop File */}
                            <div className="mb-3">
                                <div className="d-flex justify-content-between align-items-center mb-1">
                                    <label className="Sale-management-salae-field-label">Banner Image</label>
                                    <span className="Sale-management-salae-upload-min">Min: 1920×600px</span>
                                </div>

                                {desktopPreviewUrl ? (
                                    <div className="Sale-management-salae-img-preview-wrap">
                                        <img src={desktopPreviewUrl} alt="Banner preview" />
                                        <button
                                            className="Sale-management-salae-img-remove"
                                            onClick={removeDesktopFile}
                                            title="Remove image"
                                        >
                                            <RiCloseLine />
                                        </button>
                                    </div>
                                ) : (
                                    <label className="Sale-management-salae-upload-zone" htmlFor="sal-desktop-upload">
                                        <div className="Sale-management-salae-upload-icon"><RiUpload2Line /></div>
                                        <p className="Sale-management-salae-upload-title">Click to upload or drag and drop</p>
                                        <p className="Sale-management-salae-upload-hint">PNG, JPG or WEBP (Max 2MB)</p>
                                        <Form.Control
                                            id="sal-desktop-upload"
                                            type="file"
                                            accept="image/png,image/jpeg,image/webp"
                                            style={{ display: 'none' }}
                                            onChange={(e) => {
                                                const file = e.target.files[0];
                                                if (file) {
                                                    setDesktopFile(file);
                                                    setDesktopPreviewUrl(URL.createObjectURL(file));
                                                }
                                            }}
                                        />
                                    </label>
                                )}
                            </div>

                        </div>

                        {/* Form Footer */}
                        <div className="Sale-management-salae-form-footer">
                            <button
                                className="Sale-management-salae-cancel-btn btn"
                                onClick={() => setCurrentView('list')

                                }
                            >
                                Cancel
                            </button>

                            {addBannerLoading ? (
                                <div className="d-flex justify-content-center align-items-center" style={{ flex: 1 }}>
                                    <Spinner animation="border" size="sm" style={{ color: 'var(--primary-color)' }} />
                                </div>
                            ) : (
                                <button
                                    className="Sale-management-salae-publish-btn btn"
                                    onClick={() => {
                                        setAddBannerLoading(true);
                                        if (btnLabel === 'Publish Banner') {
                                            addBanner();
                                        } else {
                                            updateBanner();
                                        }
                                    }}
                                >
                                    {btnLabel}
                                </button>
                            )}
                        </div>
                    </div>

                </div>
            </Container>
        </div>
    );
};

export default SaleManagement2;